import React from 'react';
import { Dialog, DialogContent, Button } from '@leadsnavi/ui-kit';
import { X } from 'lucide-react';
import { ProductInfo } from './types';
import { useDialogVisible } from '@/hooks';
import { TruncatedTextWithTooltip } from '@/components/truncated-text-with-tooltip';

interface InquiryPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productInfo: ProductInfo;
}

const InquiryPreviewDialog: React.FC<InquiryPreviewDialogProps> = ({ open, onOpenChange, productInfo }) => {
  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl gap-0 p-0 border-none" showCloseButton={false}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">View inquiry details</h2>
          <Button variant="ghost" size="sm" onClick={handleClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="px-6 pt-5 pb-7.5">
          {/* Led Product Info Table */}
          <div className="border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="bg-[#EFEFEF] px-4 py-2 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900"> Product Info</h3>
            </div>

            {/* Table Body */}
            <div className="bg-white">
              {/* Row 1: Product Name & Order Quantity */}
              <div className="grid grid-cols-[150px_1fr_150px_1fr] border-b border-gray-200">
                {/* Product Name */}
                <div className="w-[150px]  px-4 border-r border-gray-200 flex items-center bg-[#FAFAFA]">
                  <div className="text-xs text-[#262626] text-right font-normal leading-[150%] tracking-[0.18px] w-full">
                    Product Name
                  </div>
                </div>
                <div className="px-4 py-3 border-r border-gray-200 flex items-center min-w-0">
                  <TruncatedTextWithTooltip
                    text={productInfo.productName}
                    className="text-xs text-[#262626] font-normal leading-[150%] tracking-[0.18px] w-full min-w-0"
                  />
                </div>
                {/* Order Quantity */}
                <div className="w-[150px]  px-4 border-r border-gray-200 flex items-center bg-[#FAFAFA]">
                  <div className="text-xs text-[#262626] text-right font-normal leading-[150%] tracking-[0.18px] w-full">
                    Order Quantity
                  </div>
                </div>
                <div className="px-4 py-3 flex items-start min-w-0">
                  <div className="text-xs text-[#262626] font-normal leading-[150%] tracking-[0.18px] break-words w-full min-w-0">
                    <TruncatedTextWithTooltip
                      text={productInfo.orderQuantity || '-'}
                      className="text-xs text-[#262626] font-normal leading-[150%] tracking-[0.18px] w-full min-w-0"
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Specification & Model No. */}
              <div className="grid grid-cols-[150px_1fr_150px_1fr] border-b border-gray-200">
                {/* Specification */}
                <div className="w-[150px]  px-4 border-r border-gray-200 flex items-center bg-[#FAFAFA]">
                  <div className="text-xs text-[#262626] text-right font-normal leading-[150%] tracking-[0.18px] w-full">
                    Specification
                  </div>
                </div>
                <div className="px-4 py-3 border-r border-gray-200 flex items-center min-w-0">
                  <TruncatedTextWithTooltip
                    text={productInfo.specifications}
                    className="text-xs text-[#262626] font-normal leading-[150%] tracking-[0.18px] w-full min-w-0"
                  />
                </div>
                {/* Model No. */}
                <div className="w-[150px]  px-4 border-r border-gray-200 flex items-center bg-[#FAFAFA]">
                  <div className="text-xs text-[#262626] text-right font-normal leading-[150%] tracking-[0.18px] w-full">
                    Model No.
                  </div>
                </div>
                <div className="px-4 py-3 flex items-start min-w-0">
                  <div className="text-xs text-[#262626] font-normal leading-[150%] tracking-[0.18px] break-words w-full min-w-0">
                    <TruncatedTextWithTooltip
                      text={productInfo.modelNo || '-'}
                      className="text-xs text-[#262626] font-normal leading-[150%] tracking-[0.18px] w-full min-w-0"
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Notes */}
              <div className="grid grid-cols-[150px_1fr]">
                <div className="w-[150px]  px-4 border-r border-gray-200 flex items-center bg-[#FAFAFA]">
                  <div className="text-xs text-[#262626] text-right font-normal leading-[150%] tracking-[0.18px] w-full">
                    Notes
                  </div>
                </div>
                <div className="px-4 py-3 flex items-start min-w-0">
                  <div className="text-xs text-[#262626] font-normal leading-[150%] tracking-[0.18px] max-h-[152px] overflow-y-auto break-words w-full min-w-0">
                    {productInfo.notes || '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        {/* <div className="flex justify-end px-6 py-4 border-t border-gray-200">
          <Button onClick={handleClose}>Close</Button>
        </div> */}
      </DialogContent>
    </Dialog>
  );
};

export const InquiryPreviewDialogV2 = (props: InquiryPreviewDialogProps) => {
  const openV2 = useDialogVisible(props.open);
  if (!openV2) {
    return null;
  }
  return <InquiryPreviewDialog {...props} />;
};

export default InquiryPreviewDialog;
